import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const MedicalSociologyCoursePage: React.FC = () => {
  const medicalSociologyData = {
    title: "Bachelor of Science in Medical Sociology",
    shortName: "B.Sc Med Soc",
    duration: "3 Years",
    description: "An interdisciplinary program combining sociology and healthcare, focusing on understanding social factors affecting health, illness, and healthcare systems.",
    overview: "B.Sc Medical Sociology prepares students to understand the complex relationship between society and health. The program covers health behavior, healthcare systems, social determinants of health, epidemiology, and healthcare policy. Students learn to analyze how social, economic, cultural, and environmental factors influence health outcomes and healthcare delivery, preparing them for roles in public health, healthcare administration, and social research.",
    eligibility: [
      "Completed 10+2 in any stream (Science/Arts/Commerce)",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in social sciences and healthcare",
      "Good analytical and communication skills",
      "Empathy and social awareness"
    ],
    careerProspects: {
      roles: [
        "Public Health Analyst",
        "Healthcare Program Coordinator",
        "Social Research Assistant",
        "Community Health Worker",
        "Health Education Specialist",
        "Healthcare Policy Researcher",
        "Medical Social Worker",
        "Epidemiology Assistant",
        "Health Promotion Officer",
        "Healthcare Quality Analyst"
      ],
      industries: [
        "Public Health Departments",
        "Healthcare Organizations",
        "NGOs and NPOs",
        "Research Institutions",
        "Government Health Programs",
        "International Health Organizations",
        "Healthcare Consulting",
        "Educational Institutions",
        "Policy Think Tanks",
        "Community Health Centers"
      ],
      averageSalary: "₹3-6 LPA (varies by sector and experience)",
      topRecruiters: [
        "World Health Organization",
        "UNICEF",
        "Government Health Departments",
        "AIIMS Public Health",
        "Tata Institute of Social Sciences",
        "Public Health Foundation of India",
        "PATH India",
        "Healthcare NGOs",
        "Research Organizations",
        "Healthcare Consulting Firms"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Introduction to Sociology",
          "Basic Health Concepts",
          "Statistics for Social Sciences",
          "Research Methods",
          "Human Behavior",
          "Communication Skills",
          "Computer Applications",
          "Introduction to Public Health"
        ]
      },
      {
        year: 2,
        subjects: [
          "Medical Sociology Theory",
          "Epidemiology",
          "Health Systems and Policy",
          "Social Determinants of Health",
          "Health Behavior",
          "Community Health",
          "Healthcare Economics",
          "Qualitative Research Methods"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Medical Sociology",
          "Health Program Planning",
          "Healthcare Quality Management",
          "Global Health Issues",
          "Health Communication",
          "Field Work/Internship",
          "Thesis/Research Project",
          "Professional Ethics"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Medical Sociology",
      "Merit-based selection or entrance examination",
      "Group discussion or interview",
      "Document verification",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic training",
      "Field work and internship coordination"
    ],
    topColleges: [
      {
        name: "Tata Institute of Social Sciences",
        location: "Mumbai, Maharashtra",
        fees: "₹50,000 PA",
        ranking: "Premier Social Sciences Institute"
      },
      {
        name: "Jamia Millia Islamia",
        location: "New Delhi",
        fees: "₹25,000 PA",
        ranking: "Central University"
      },
      {
        name: "Jawaharlal Nehru University",
        location: "New Delhi",
        fees: "₹5,000 PA",
        ranking: "Central University"
      },
      {
        name: "University of Hyderabad",
        location: "Hyderabad, Telangana",
        fees: "₹15,000 PA",
        ranking: "Central University"
      },
      {
        name: "Christ University",
        location: "Bangalore, Karnataka",
        fees: "₹1.5 LPA",
        ranking: "Deemed University"
      }
    ],
    skills: [
      "Research and Analysis",
      "Data Collection",
      "Statistical Analysis",
      "Report Writing",
      "Communication Skills",
      "Critical Thinking",
      "Project Management",
      "Community Engagement",
      "Policy Analysis",
      "Cultural Sensitivity",
      "Problem Solving",
      "Teamwork"
    ],
    futureScope: "Growing awareness of social determinants of health and health equity is creating new opportunities. Future careers include health policy analysis, digital health behavior research, community health program design, and specialization in areas like mental health sociology, aging and health, or health technology assessment. The COVID-19 pandemic has highlighted the importance of understanding social factors in health.",
    faqs: [
      {
        question: "What is medical sociology and how is it different from regular sociology?",
        answer: "Medical sociology specifically focuses on the intersection of society and health, studying how social factors like class, race, gender, and culture affect health outcomes, illness experience, and healthcare systems."
      },
      {
        question: "Can I pursue higher studies after this degree?",
        answer: "Yes, graduates can pursue M.A./M.Sc. in Medical Sociology, Public Health, Social Work, Health Administration, or related fields. Many also pursue Ph.D. in medical sociology or public health."
      },
      {
        question: "What kind of research opportunities are available?",
        answer: "Research opportunities include health behavior studies, healthcare access research, disease prevention programs, health policy analysis, and community health interventions."
      },
      {
        question: "Is this field only for those wanting to work in healthcare?",
        answer: "No, graduates can work in various sectors including social research, policy analysis, education, NGOs, government programs, and consulting, wherever understanding of health and society intersection is valuable."
      },
      {
        question: "What is the scope for international work?",
        answer: "Excellent scope with international health organizations, global health programs, development agencies, and cross-cultural health research projects, especially in areas like health equity and global health policy."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {medicalSociologyData.title} ({medicalSociologyData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={medicalSociologyData} />
      </div>
    </div>
  );
};

export default MedicalSociologyCoursePage;